import { Link } from 'react-router-dom';
import {
  ArrowRight, Check, Code, Layout, Smartphone, Zap, Rocket, ShieldCheck, Headphones,
  MessagesSquare, PenTool, Hammer, Users
} from 'lucide-react';
import ProjectScrollReveal from '../components/ProjectScrollReveal';
import BlogCard from '../components/BlogCard';
import { projects } from '../data/projects';
import { posts } from '../data/blog';
import './Home.css';

const latestPosts = [...posts].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 3);

const services = [
  { title: 'Web Development', desc: 'Modern, high-performance websites and web applications.', icon: <Layout /> },
  { title: 'SaaS Products', desc: 'Scalable cloud software solutions built for growth.', icon: <Zap /> },
  { title: 'Custom Software', desc: 'Tailored enterprise and college ERP systems.', icon: <Code /> },
  { title: 'Mobile Apps', desc: 'Native and cross-platform mobile experiences.', icon: <Smartphone /> }
];

const highlights = [
  { title: 'Built for real users', desc: 'Products used by students, organizers and institutions, not throwaway demos.', icon: <Users /> },
  { title: 'Fast iteration', desc: 'Short build cycles so you see working software early and can steer as we go.', icon: <Zap /> },
  { title: 'Support after launch', desc: 'We stay on after go-live for fixes, improvements and new features.', icon: <Headphones /> }
];

const steps = [
  { title: 'Discover', desc: 'We learn your goals, users and constraints, then agree scope and timeline.', icon: <MessagesSquare /> },
  { title: 'Design', desc: 'Wireframes and UI concepts you can review and approve before we build.', icon: <PenTool /> },
  { title: 'Build', desc: 'Iterative development with regular demos so you always see progress.', icon: <Hammer /> },
  { title: 'Launch & support', desc: 'Deployment, handover and ongoing support as your product grows.', icon: <Rocket /> }
];

// slug = simple-icons name, color = brand hex (null = follows the theme text colour)
const stack = [
  { name: 'React', slug: 'react', color: '61DAFB' },
  { name: 'Next.js', slug: 'nextdotjs', color: null },
  { name: 'TypeScript', slug: 'typescript', color: '3178C6' },
  { name: 'Node.js', slug: 'nodedotjs', color: '5FA04E' },
  { name: 'Python', slug: 'python', color: '3776AB' },
  { name: 'Flutter', slug: 'flutter', color: '02569B' },
  { name: 'PostgreSQL', slug: 'postgresql', color: '4169E1' },
  { name: 'MongoDB', slug: 'mongodb', color: '47A248' },
  { name: 'Firebase', slug: 'firebase', color: 'FFCA28' },
  { name: 'Docker', slug: 'docker', color: '2496ED' },
  { name: 'Vercel', slug: 'vercel', color: null },
  { name: 'Tailwind CSS', slug: 'tailwindcss', color: '06B6D4' }
];

const Home = () => {
  return (
    <div className="home-page fade-in">
      <title>Pidesk - College ERP, School & Learning App Development</title>
      <meta name="description" content="Pidesk builds college ERP software, school management systems, learning apps and websites for Indian institutes and businesses. See our work and pricing." />

      {/* Hero */}
      <section className="hero-section">
        <div className="hero-bg-glow"></div>
        <div className="container hero-container">
          <div className="badge glass fade-in" style={{ animationDelay: '0.1s' }}>
            <span>Innovation led Agency</span>
          </div>
          <h1 className="hero-title fade-in" style={{ animationDelay: '0.2s' }}>
            We build software that <br className="hero-br" />
            <span className="text-highlight">grows your business.</span>
          </h1>
          <div className="hero-actions fade-in" style={{ animationDelay: '0.4s' }}>
            <Link to="/contact" className="btn btn-primary btn-lg">
              Start a Project <ArrowRight size={18} style={{ marginLeft: 8 }} />
            </Link>
            <Link to="/products" className="btn btn-secondary btn-lg">See our work</Link>
          </div>

          <ul className="hero-stats fade-in" style={{ animationDelay: '0.5s' }}>
            <li><strong>{projects.length}+</strong><span>Products built</span></li>
            <li><strong>Web · Mobile · SaaS</strong><span>What we ship</span></li>
            <li><strong>End-to-end</strong><span>Design to support</span></li>
          </ul>
        </div>
      </section>

      {/* Services */}
      <section className="section services-section bg-secondary">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What We Do</h2>
            <p className="section-subtitle">Comprehensive tech solutions tailored to your unique challenges.</p>
          </div>

          <div className="services-grid">
            {services.map((service) => (
              <div key={service.title} className="service-card glass hover-lift">
                <div className="service-icon">{service.icon}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-desc">{service.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center" style={{ marginTop: '48px' }}>
            <Link to="/services" className="link-arrow">See all services <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* Projects */}
      <ProjectScrollReveal />

      {/* Why Pidesk */}
      <section className="section why-section">
        <div className="container">
          <div className="why-header">
            <span className="why-eyebrow">Why Pidesk</span>
            <h2 className="why-title">Built for speed &amp; quality</h2>
            <p className="why-subtitle">Everything you need to go from idea to a product people use.</p>
          </div>

          <div className="why-cards">
            {/* 1. Idea to launch */}
            <article className="why-card tone-yellow">
              <div className="why-card-text">
                <h3>Idea to launch</h3>
                <p>One team for design, development and deployment.</p>
              </div>
              <div className="why-art">
                <div className="mock mock-timeline">
                  <div className="mock-title">Project timeline</div>
                  <div className="tl-row done"><span className="tl-dot"><Check size={11} /></span>Discover<em>Done</em></div>
                  <div className="tl-row done"><span className="tl-dot"><Check size={11} /></span>Design<em>Done</em></div>
                  <div className="tl-row active"><span className="tl-dot"></span>Build<div className="tl-bar"><i></i></div></div>
                  <div className="tl-row"><span className="tl-dot"></span>Launch<em>Next</em></div>
                </div>
                <div className="mock-pill pill-float"><Rocket size={14} /> Launch ready</div>
              </div>
            </article>

            {/* 2. Clear communication */}
            <article className="why-card tone-blue">
              <div className="why-card-text">
                <h3>Clear communication</h3>
                <p>Regular demos, honest timelines, one point of contact.</p>
              </div>
              <div className="why-art">
                <div className="mock mock-chat">
                  <div className="bubble in">Can we add certificates to the events app?</div>
                  <div className="bubble out">Yes. It's live on staging, take a look.</div>
                  <div className="bubble in short">Perfect, ship it!</div>
                </div>
                <div className="mock-pill pill-float"><MessagesSquare size={14} /> Weekly demo</div>
              </div>
            </article>

            {/* 3. Reliable & secure */}
            <article className="why-card tone-green">
              <div className="why-card-text">
                <h3>Reliable &amp; secure</h3>
                <p>Secure defaults and code you can maintain and grow.</p>
              </div>
              <div className="why-art">
                <div className="mock mock-status">
                  <div className="st-row"><span>HTTPS / SSL</span><b className="on"><i></i>Enabled</b></div>
                  <div className="st-row"><span>Daily backups</span><b className="on"><i></i>Enabled</b></div>
                  <div className="st-row"><span>Role-based access</span><b className="on"><i></i>Enabled</b></div>
                  <div className="st-row"><span>Uptime monitoring</span><b><i></i>Setup</b></div>
                </div>
                <div className="mock-pill pill-float"><ShieldCheck size={14} /> Secure by default</div>
              </div>
            </article>
          </div>

          <div className="why-mini">
            {highlights.map((h) => (
              <div key={h.title} className="why-mini-item">
                <div className="why-icon">{h.icon}</div>
                <div>
                  <h4>{h.title}</h4>
                  <p>{h.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section process-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">How we work</h2>
            <p className="section-subtitle">A simple, transparent process from first call to launch day.</p>
          </div>
          <ol className="process-grid">
            {steps.map((s, i) => (
              <li key={s.title} className="process-step">
                <span className="process-num">{String(i + 1).padStart(2, '0')}</span>
                <div className="process-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Blog teaser */}
      <section className="section blog-teaser-section bg-secondary">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">From the blog</h2>
            <p className="section-subtitle">Guides on ERP, school management, online testing and learning apps.</p>
          </div>

          <div className="blog-grid">
            {latestPosts.map((post) => <BlogCard key={post.slug} post={post} />)}
          </div>

          <div className="text-center" style={{ marginTop: '40px' }}>
            <Link to="/blog" className="link-arrow">Read the blog <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* Tech stack */}
      <section className="section stack-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title tools-title"><span>Tools we build with</span></h2>
          </div>
        </div>

        {/* Two copies of the list so the loop is seamless */}
        <div className="marquee" aria-label="Technologies we use">
          <div className="marquee-track">
            {[...stack, ...stack].map((t, i) => (
              <div key={`${t.slug}-${i}`} className="tech-item" aria-hidden={i >= stack.length}>
                <span
                  className="tech-logo"
                  style={{
                    WebkitMaskImage: `url(https://cdn.jsdelivr.net/npm/simple-icons@13/icons/${t.slug}.svg)`,
                    maskImage: `url(https://cdn.jsdelivr.net/npm/simple-icons@13/icons/${t.slug}.svg)`,
                    backgroundColor: t.color ? `#${t.color}` : 'var(--text-primary)'
                  }}
                />
                <span>{t.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-card">
            <h2>Ready to build something great?</h2>
            <p>Tell us about your project and we'll get back to you with a plan, timeline and estimate.</p>
            <div className="hero-actions">
              <Link to="/contact" className="btn btn-primary btn-lg">Start a Project</Link>
              <Link to="/pricing" className="btn btn-secondary btn-lg">View pricing</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
